import { $, $$, MAX_PAGINAS, PARCIAL, RECHAZO, TOPE_SERVIDOR, alcance, aviso, avisoHtml, cajaDelActo, desfaseDe, desfaseLocal, el, esc, fecha, limpiar, misCajas, noHabilitado, num, ordenarPor, pedir, pedirTodo, puede, puedoActo, puedoOperar, rpc, rpcLee, sb } from './nucleo';
import { catalogosCobro, rotuloMoneda } from './cobros';

// =====================================================================
//  COMISIONES Y LIQUIDACIONES  (0034 / 0035, punto 5)
// =====================================================================
//  Tres pantallas en una pestaña, cada una con su permiso:
//
//    · Mis comisiones ....... `comision.ver`        — qué vendí y qué me deben
//    · Las tarifas .......... `comision.configurar` — cuánto se paga por qué
//    · Las liquidaciones .... `liquidacion.*`       — la quincena y su pago
//
//  Acá no se decide nada. La tarifa que gana la elige `comision_de` por
//  especificidad (D56), la moneda la fija la liquidación (D57), el pago lo
//  frena la caja si no tiene el saldo (D59) y la vigencia arranca el día de
//  la empresa (D60). Todo eso vive en la base: esta pantalla lee dos vistas
//  y llama a seis funciones. Si un botón no aparece es porque falta el
//  permiso; si aparece y la base igual dice que no, el mensaje que se ve es
//  el de la base.
//
//  Regla: las funciones `cargar…` sólo TRAEN; se pinta cuando terminaron todas (la tabla de tarifas
//  resuelve nombres con listas de otra consulta y, si pintaba primero, mostraba ids).
let COMIS = [], ESQUEMAS = [], HIST_ESQ = [], LIQS = [], LIQ_ABIERTA = null;
let CATEGORIAS = [], PRODUCTOS = [], PERSONAS = [], ROLES_COM = [], CAJAS_LIQ = [];

//  Las lecturas de catálogo se traían con `data || []` sin mirar `error`: la regla del 4/9 al revés (el RLS
//  contesta lista vacía y un fallo contesta error, y se veían igual).
let ERROR_PERSONAS = false, ERROR_CATALOGOS = false, ERROR_CAJAS = false;

//  Las CUATRO lecturas de datos también: con `v_comisiones` caída se pintaban totales de plata en CERO y
//  "Todavía no hay ventas que comisionen" (D83). Guardan el MENSAJE, no un booleano, porque el cartel es el
//  de la base (D68), y vacían su arreglo: en una reentrada, las filas viejas serían datos viejos.
let ERROR_COMIS = '', ERROR_ESQ = '', ERROR_HIST = '', ERROR_LIQS = '';

//  `puede` contesta sí o no; el alcance (lo suyo o lo de todos) viene en la misma fila. Vive en el núcleo
//  porque la necesitan dos pantallas (D85): dos implementaciones son una que se rompe sin avisar a la otra.

//  D83: un valor de respaldo no habla. Con la lista de personas caída NADIE está en ella: un guión diría
//  "sin persona" de todos. Un id ausente de una lista que SÍ se leyó es alguien desactivado.
const nombreDe = id => {
  if (id == null) return '—';
  const p = PERSONAS.find(x => x.id === id);
  if (p) return p.nombre;
  return ERROR_PERSONAS ? 'no se pudo leer quién' : `persona #${id}`;
};

//  `comisiones_visibles()` (de donde salen `v_comisiones` y `comisiones_del_periodo`, 0053) devuelve TODO
//  también a quien tenga `comision.configurar:todos`. Cerrar de más también es un bug (corolario de la D86).
const veComisiones_ = () =>
  puede('comision','ver') || alcance('comision','configurar') === 'todos';

//  La misma regla para la tabla: con `comision.configurar:todos` y `comision.ver:propio` la vista trae las
//  ventas de todos, así que va la columna del vendedor y no "no hay ventas TUYAS". Es la D85 con el signo
//  invertido: una regla aplicada a un permiso y no al otro.
const veTodasLasComisiones = () =>
  alcance('comision','configurar') === 'todos' ||
  alcance('comision','ver') !== 'propio';

const CHIP_SITUACION = {
  'pagada'          : 'c-ok',
  'en liquidación'  : 'c-warn',
  'liquidable'      : 'c-pen',
  'todavía no'      : ''
};
const CHIP_LIQ = {
  borrador: 'c-pen', aprobada: 'c-warn', pagada: 'c-ok', anulada: 'c-bad'
};
const NOMBRE_TIPO = {
  comision_vendedor: 'Comisión de vendedor',
  viajes_motoquero : 'Viajes de motoquero'
};

// ---------------------------------------------------------------------
//  La carga
// ---------------------------------------------------------------------
export async function cargarComisiones(){
  ['#cmMsg','#esqMsg','#histEsqMsg','#liqMsg','#genMsg','#liqDetMsg'].forEach(limpiar);
  //  Cierra la tarjeta del detalle al entrar y la reabre al final si había una; si no, un detalle con error
  //  (`LIQ_ABIERTA` en null) queda en pantalla sin el cartel que lo explicaba.
  const abierta = LIQ_ABIERTA;
  cerrarDetalle();
  await catalogosCobro();
  //  Igual que en Cuentas: con `config` ilegible, este rótulo afirmaría una
  //  moneda que nadie leyó arriba de cada liquidación.
  $('#cmMoneda').textContent = rotuloMoneda();

  const veComisiones = veComisiones_();
  const configura    = puede('comision','configurar');
  const genera       = puede('liquidacion','generar');
  const paga         = puede('liquidacion','pagar');

  $('#cardMisComisiones').classList.toggle('oculto', !veComisiones);
  $('#cardTarifas').classList.toggle('oculto', !configura);
  //  El historial dice cuánto cobra cada uno y quién se lo cambió: es del
  //  que configura, no del que mira lo suyo. La base todavía se lo deja
  //  ver a `comision.ver`; eso se aprieta en una migración aparte.
  $('#cardHistTarifas').classList.toggle('oculto', !configura);
  $('#cardGenerarLiq').classList.toggle('oculto', !genera);

  //  Las personas las puede leer cualquiera que esté dado de alta
  //  (`usuarios_ver` no pide más que eso): hacen falta para el historial,
  //  que guarda el id de quién tocó la tarifa, y para elegir a quién se
  //  le liquida.
  const tareas: PromiseLike<unknown>[] = [
    pedir(sb.from('usuarios').select('id,nombre').eq('activo', true).order('nombre'))
      .then(({ data, error }) => { ERROR_PERSONAS = !!error; PERSONAS = data || []; }),
    cargarLiquidaciones()
  ];
  if (veComisiones) tareas.push(cargarMisComisiones());
  if (configura) tareas.push(cargarEsquemas(), cargarHistorialEsquemas(), cargarCatalogosTarifa());
  //  La caja del pago se puede pisar, pero sólo la elige quien ve las
  //  cajas. Al que no, la base le usa la de la configuración.
  if (paga && puede('caja','ver'))
    tareas.push(pedir(sb.from('cajas').select('id,nombre').eq('activo', true).order('orden'))
      .then(({ data, error }) => { ERROR_CAJAS = !!error; CAJAS_LIQ = data || []; }));
  //  Y cuáles de esas cajas puedo OPERAR, que es otra pregunta: verlas es
  //  `caja.ver` y pagar desde ellas es la habilitación por caja (D48, D93).
  //  También hace falta sin `caja.ver`: sin elegir, el pago sale de la caja
  //  que dice la configuración, y ésa también puede no ser mía.
  if (paga) tareas.push(misCajas());

  await Promise.all(tareas);

  //  Recién acá, con todo traído, se pinta: así ninguna tabla depende de
  //  qué consulta llegó primero.
  if (veComisiones) pintarMisComisiones();
  if (configura){ pintarSelectsTarifa(); pintarEsquemas(); pintarHistorialEsquemas(); }
  if (genera){
    $('#glPersona').innerHTML = PERSONAS.map(p =>
      `<option value="${p.id}">${esc(p.nombre)}</option>`).join('');
    //  `#glPersona` es `required`: sin una `<option>` el botón no hace nada visible. Son DOS causas (la lectura
    //  falló, o no hay nadie activo) y no se dicen igual (D87).
    $('#glOk').disabled = !PERSONAS.length;
    if (ERROR_PERSONAS)
      aviso('#genMsg','bad', 'No se pudo leer la lista de personas, así que no ' +
        'se puede elegir a quién liquidar. Volvé a entrar a la pestaña.');
    else if (!PERSONAS.length)
      aviso('#genMsg','warn', 'No hay ninguna persona activa a quien liquidarle.');
    if (!$('#glDesde').value) quincena(0);
  }
  pintarLiquidaciones();
  if (abierta) await abrirLiquidacion(abierta, true);
}

// =====================================================================
//  1 · MIS COMISIONES
// =====================================================================
//
//  EL PERÍODO: se lee un período entero (`pedirTodo`, de a páginas), no las 1.000 más nuevas: el tope del
//  servidor cortaba los totales de PLATA a unos días sin decirlo (D122). Los totales dicen de qué fechas son.
//  Arranca en la quincena de hoy. El día es el de la EMPRESA (D39): el rango va de la medianoche de `desde`
//  a la de `hasta + 1` con el desfase de `empresa.zona_horaria`.
let PEDIDO_COMIS = 0;
//  'leyendo' mientras viaja: la tabla y los totales de antes no se muestran
//  como si fueran los del período nuevo.
let ESTADO_COMIS: 'inicial' | 'leyendo' | 'ok' | 'error' = 'inicial';
//  El período tiene más comisiones de las que se leen de una (`PARCIAL`).
let COMIS_PARCIAL = false;
//  La zona de la empresa, leída en cada carga (es una fila): si falló, el período se cuenta con el reloj de
//  esta computadora y se DICE (igual que la Rentabilidad).
let ZONA_CM: string | null = null, E_ZONA_CM = '';
let PERIODO_LOCAL = false;
async function leerZona(){
  const { data, error } = await pedir(sb.from('config').select('clave,valor')
    .eq('clave', 'empresa.zona_horaria'));
  if (error){ E_ZONA_CM = error.message; ZONA_CM = null; return; }
  E_ZONA_CM = '';
  const v = (data || [])[0]?.valor;
  //  Sin la fila, la base cuenta el día en UTC (`app.dia_empresa()`): lo mismo.
  ZONA_CM = v == null ? 'UTC' : ((typeof v === 'string' ? v : JSON.stringify(v)).trim() || 'UTC');
}
const isoDia = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
const hoyEmpresa = (): string => {
  if (ZONA_CM){
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_CM, year: 'numeric',
        month: '2-digit', day: '2-digit' }).format(new Date());
    } catch { /* zona desconocida: abajo */ }
  }
  const d = new Date();
  return isoDia(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())));
};
const diaSiguiente = (iso: string) => {
  const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + 1); return isoDia(d);
};
//  La quincena que contiene a HOY: del 1 al 15, o del 16 al último día.
function estaQuincena(){
  const h = hoyEmpresa();
  const [y, m, d] = h.split('-').map(Number);
  const desde = d <= 15 ? `${h.slice(0, 8)}01` : `${h.slice(0, 8)}16`;
  const hasta = d <= 15 ? `${h.slice(0, 8)}15` : isoDia(new Date(Date.UTC(y, m, 0)));
  $('#cmDesde').value = desde;
  $('#cmHasta').value = hasta;
}
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
//  Año de cuatro cifras desde el 2000: mientras se TECLEA, el campo pasa por `0002-08-15` y similares, y
//  cada uno es una fecha válida que abarca toda la historia.
const periodoValido = () => {
  const d = $('#cmDesde').value, h = $('#cmHasta').value;
  return !!d && !!h && d <= h && d >= '2000-01-01';
};

async function cargarMisComisiones(){
  const n = ++PEDIDO_COMIS;
  await leerZona();
  if (n !== PEDIDO_COMIS) return false;
  if (!$('#cmDesde').value && !$('#cmHasta').value) estaQuincena();
  if (!periodoValido()){
    //  Sin un período no se lee nada, y no se dice «no hay»: se pide el período.
    //  Y el cartel de una lectura anterior que falló se va: no es de éste.
    ESTADO_COMIS = 'ok'; ERROR_COMIS = ''; COMIS = []; COMIS_PARCIAL = false;
    limpiar('#cmMsg');
    return false;
  }
  const desde = $('#cmDesde').value, hasta = $('#cmHasta').value;
  const off = (ZONA_CM && desfaseDe(ZONA_CM, desde)) || null;
  PERIODO_LOCAL = !off;
  const d0 = off ?? desfaseLocal(desde);
  const d1 = (ZONA_CM && desfaseDe(ZONA_CM, diaSiguiente(hasta))) || desfaseLocal(diaSiguiente(hasta));
  ESTADO_COMIS = 'leyendo';
  pintarFilasComisiones();
  //  El período va ADENTRO de la función (0053): la vista calculaba toda la historia y recién después
  //  filtraba. La clave de la página siguiente también (`p_despues`, `claveAdentro`).
  const r = await pedirTodo(ultima => rpcLee('comisiones_del_periodo', {
      p_desde: `${desde}T00:00:00${d0}`,
      p_hasta: `${diaSiguiente(hasta)}T00:00:00${d1}`,
      ...(ultima == null ? {} : { p_despues: ultima }) }),
    { por: 'pedido_item_id', claveAdentro: true,
      orden: ordenarPor('-ocurrido_en', '-pedido_item_id'),
      //  Una lectura que ya nadie espera deja de pedir páginas.
      vigente: () => n === PEDIDO_COMIS });
  //  La respuesta vieja no pisa a la nueva (cambiar el período dos veces manda dos lecturas).
  if (n !== PEDIDO_COMIS) return false;
  if (r.error){
    //  Más de las que se leen de una: ni la tabla ni los totales, que serían
    //  de una parte. Se dice qué hacer.
    COMIS_PARCIAL = r.error.code === PARCIAL;
    ESTADO_COMIS = 'error'; ERROR_COMIS = r.error.message; COMIS = [];
    if (!COMIS_PARCIAL) aviso('#cmMsg','bad', r.error.message);
    else limpiar('#cmMsg');
    return false;
  }
  ESTADO_COMIS = 'ok'; ERROR_COMIS = ''; COMIS = r.data || []; COMIS_PARCIAL = false;
  limpiar('#cmMsg');
  return true;
}
//  Se pinta siempre al volver: si esta lectura quedó vieja, lo que se pinta es
//  el estado de la nueva («leyendo…»), que es lo cierto.
const recargarPeriodo = async () => { await cargarMisComisiones(); pintarMisComisiones(); };
//  Y las fechas tecleadas esperan a que se deje de teclear.
let DEMORA_PERIODO: ReturnType<typeof setTimeout> | null = null;
const alCambiarFecha = () => {
  if (DEMORA_PERIODO) clearTimeout(DEMORA_PERIODO);
  DEMORA_PERIODO = setTimeout(() => { DEMORA_PERIODO = null; recargarPeriodo(); }, 400);
};
$('#cmDesde').onchange = alCambiarFecha;
$('#cmHasta').onchange = alCambiarFecha;
//  «Esta quincena» lee en el acto, y se lleva la espera de una fecha que se
//  estaba tecleando: si no, esa espera vencía después y releía lo mismo.
$('#cmQuincena').onclick = () => {
  if (DEMORA_PERIODO){ clearTimeout(DEMORA_PERIODO); DEMORA_PERIODO = null; }
  estaQuincena(); recargarPeriodo();
};

function pintarMisComisiones(){
  //  La columna del vendedor sólo tiene sentido para quien ve más de uno.
  //  Al vendedor con alcance propio la vista ya le devuelve nada más lo
  //  suyo: esconder la columna no es la seguridad, es no mostrar de más.
  const varios = veTodasLasComisiones();
  $('#thVendedor').classList.toggle('oculto', !varios);
  $('#cmVendedor').parentElement.classList.toggle('oculto', !varios);
  if (varios){
    const nombres = [...new Set(COMIS.map(c => c.vendedor).filter(Boolean))].sort();
    const antes = $('#cmVendedor').value;
    $('#cmVendedor').innerHTML = '<option value="">Todos</option>' +
      nombres.map(n => `<option${n === antes ? ' selected' : ''}>${esc(n)}</option>`).join('');
  }
  pintarFilasComisiones();
}

function comisionesFiltradas(){
  const q   = ($('#cmBusca').value || '').toLowerCase();
  const sit = $('#cmSituacion').value;
  const ven = $('#cmVendedor').value;
  return COMIS.filter(c =>
    (!sit || c.situacion === sit) &&
    (!ven || c.vendedor === ven) &&
    (!q || [c.pedido, c.producto, c.producto_codigo, c.categoria, c.vendedor]
             .some(t => (t || '').toLowerCase().includes(q))));
}

//  D85: el alcance también afirma. Con `comision.ver:propio` la vista ya recortó la lista a sus ventas:
//  "todavía no hay ventas que comisionen" hablaría de toda la empresa. Las dos frases dicen de quién son los números.
function vacioComisiones(){
  //  Y si la lectura falló, no hay nada que afirmar sobre las ventas: lo único
  //  que se sabe es que no se pudo leer (regla del 4/9).
  if (COMIS_PARCIAL) return 'El período tiene demasiadas comisiones para leerlas de una vez: achicalo';
  if (ERROR_COMIS) return 'No se pudieron leer las comisiones';
  if (!periodoValido()) return 'Elegí desde y hasta para ver las comisiones de ese período';
  return veTodasLasComisiones()
    ? 'No hay ventas que comisionen en este período'
    : 'No hay ventas tuyas que comisionen en este período';
}

function pintarFilasComisiones(){
  const f = comisionesFiltradas();
  const varios = veTodasLasComisiones();
  const cols = varios ? 9 : 8;

  //  De qué fechas son los números, siempre a la vista.
  $('#cmPeriodo').innerHTML = periodoValido()
    ? `Del ${dm($('#cmDesde').value)} al ${dm($('#cmHasta').value)}${PERIODO_LOCAL
        ? ` · <span class="chip c-warn">con el reloj de esta computadora</span> ${E_ZONA_CM
            ? 'porque no se pudo leer la zona horaria de la empresa'
            : 'porque este navegador no conoce la zona horaria de la empresa'}` : ''}`
    : '';
  if (ESTADO_COMIS === 'leyendo' || ESTADO_COMIS === 'inicial'){
    $('#cmResumen').innerHTML = '';
    $('#tblComisiones').innerHTML =
      `<tr><td colspan="${cols}" class="vacio">Leyendo las comisiones del período…</td></tr>`;
    return;
  }

  //  El resumen es de lo filtrado, no del total: si alguien filtró por un
  //  vendedor, el número de arriba tiene que ser el de ese vendedor.
  const suma = s => f.filter(c => c.situacion === s)
                     .reduce((a, c) => a + Number(c.comision || 0), 0);
  //  Cuatro totales de PLATA. Con la lectura caída `f` está vacío y darían cero (D83). Y con el período MÁS
  //  GRANDE de lo que se lee (`MAX_PAGINAS`) no hay ni tabla ni totales: serían de una parte.
  $('#cmResumen').innerHTML = COMIS_PARCIAL
    ? `<span class="chip c-warn">El período tiene ${num(TOPE_SERVIDOR * MAX_PAGINAS)} ` +
      'comisiones o más, que es más de lo que se lee de una vez: no se muestra ninguna ' +
      'ni ningún total, porque serían de una parte. Achicá el período.</span>'
    : ERROR_COMIS
    ? '<span class="chip c-bad">No se pudieron leer las comisiones, así que acá no ' +
      'va ningún total: un cero sería un número que nadie leyó.</span>'
    : !periodoValido() ? ''
    : ['liquidable','en liquidación','pagada','todavía no']
      .map(s => `<span class="chip ${CHIP_SITUACION[s]}">${s}: <b>${
          num(Math.round(suma(s) * 100) / 100)} ${esc(rotuloMoneda())}</b></span>`).join(' ');

  $('#tblComisiones').innerHTML = f.map(c => `
    <tr>
      <td class="mono">${esc(fecha(c.ocurrido_en))}</td>
      <td><span class="chip">${esc(c.pedido || '—')}</span></td>
      ${varios ? `<td>${esc(c.vendedor || '—')}</td>` : ''}
      <td><b>${esc(c.producto || '—')}</b>
          <span class="hint mono">${esc(c.producto_codigo || '')}</span></td>
      <td>${esc(c.categoria || '—')}</td>
      <td class="num">${num(Number(c.cantidad))}</td>
      <td class="num"><b>${num(Number(c.comision))}</b></td>
      <td>${esc(c.esquema || '—')}</td>
      <td><span class="chip ${CHIP_SITUACION[c.situacion] || ''}">${esc(c.situacion)}</span></td>
    </tr>`).join('') ||
    `<tr><td colspan="${cols}" class="vacio">${
      COMIS.length ? 'Nada con ese filtro en este período' : esc(vacioComisiones())}</td></tr>`;
}

$('#cmBusca').oninput       = pintarFilasComisiones;
$('#cmSituacion').onchange  = pintarFilasComisiones;
$('#cmVendedor').onchange   = pintarFilasComisiones;

// =====================================================================
//  2 · LAS TARIFAS
// =====================================================================
//  La especificidad de la D56, con los mismos números que usa la base:
//  la persona pesa más que la cosa, y el producto más que la categoría.
const prioridadDe = e =>
  (e.usuario_id ? 10 : e.rol_id ? 4 : 0) + (e.producto_id ? 2 : e.categoria_id ? 1 : 0);

async function cargarEsquemas(){
  const { data, error } = await pedir(sb.from('esquemas_comision')
    .select('id,nombre,tipo,monto,porcentaje,categoria_id,producto_id,rol_id,usuario_id,vigencia_desde,vigencia_hasta,activo,motivo')
    .order('id'));
  if (error){ ERROR_ESQ = error.message; ESQUEMAS = []; aviso('#esqMsg','bad', error.message); return false; }
  ERROR_ESQ = ''; ESQUEMAS = data || [];
  return true;
}

async function cargarCatalogosTarifa(){
  const [cs, ps, rs] = await Promise.all([
    pedirTodo(() => sb.from('categorias').select('id,nombre,orden').eq('activo', true), { por: 'id', orden: ordenarPor('orden', 'id') }),
    pedirTodo(() => sb.from('productos').select('id,codigo,descripcion').eq('activo', true), { por: 'id', orden: ordenarPor('codigo', 'id') }),
    pedir(sb.from('roles').select('id,nombre').eq('activo', true).order('nombre'))
  ]);
  //  Los tres van en una sola bandera porque el formulario es uno: con cualquiera caído hay un `<select>`
  //  sin `<option>`, y un select vacío también afirma (regla del 4/9).
  ERROR_CATALOGOS = !!(cs.error || ps.error || rs.error);
  CATEGORIAS = cs.data || []; PRODUCTOS = ps.data || []; ROLES_COM = rs.data || [];
  return !ERROR_CATALOGOS;
}

function pintarSelectsTarifa(){
  $('#esCategoria').innerHTML = CATEGORIAS.map(c =>
    `<option value="${c.id}">${esc(c.nombre)}</option>`).join('');
  //  Arranca vacío (pedido de Steven, 1/11, D216): nadie elige sin querer el
  //  primero del catálogo. `reflejarTarifa` no deja fijar sin producto.
  $('#esProducto').innerHTML = (PRODUCTOS.length ? '<option value="">— elegí el producto —</option>' : '') +
    PRODUCTOS.map(p => `<option value="${p.id}">${esc(p.codigo)} — ${esc(p.descripcion)}</option>`).join('');
  $('#esRol').innerHTML = ROLES_COM.map(r =>
    `<option value="${r.id}">${esc(r.nombre)}</option>`).join('');
  $('#esUsuario').innerHTML = PERSONAS.map(p =>
    `<option value="${p.id}">${esc(p.nombre)}</option>`).join('');
  //  Un `<select>` sin `<option>` también afirma (regla del 4/9): que no hay categorías, productos, roles ni
  //  personas en la empresa. Con los catálogos caídos se veía igual que con la empresa recién instalada.
  if (ERROR_CATALOGOS || ERROR_PERSONAS)
    aviso('#esCatMsg','bad', ERROR_CATALOGOS && ERROR_PERSONAS
      ? 'No se pudieron leer los catálogos ni la lista de personas: los ' +
        'desplegables de abajo están vacíos porque falló la lectura, no porque ' +
        'no haya nada. Volvé a entrar a la pestaña.'
      : ERROR_CATALOGOS
        ? 'No se pudieron leer las categorías, los productos o los roles: esos ' +
          'desplegables están vacíos porque falló la lectura, no porque no haya ' +
          'nada. Volvé a entrar a la pestaña.'
        : 'No se pudo leer la lista de personas: el desplegable de "A una ' +
          'persona" está vacío porque falló la lectura, no porque no haya nadie.');
  else limpiar('#esCatMsg');
  reflejarTarifa();
}

//  Un id crudo como respaldo también habla: `categoría 20` se lee igual que una categoría dada de baja
//  cuando el catálogo no se pudo leer. Misma lección que `nombreDe`.
const delCatalogo = (lista, id, que, campo) => {
  const x = lista.find(y => y.id === id);
  if (x) return String(x[campo]);
  const rot = que ? que + ' ' : '';
  return ERROR_CATALOGOS ? `${rot}que no se pudo leer` : `${rot}#${id}`;
};

function destinoDe(e){
  const partes = [];
  if (e.usuario_id)   partes.push(nombreDe(e.usuario_id));
  //  El prefijo lo pone el llamador, así que `delCatalogo` recibe el rótulo vacío.
  else if (e.rol_id)  partes.push('rol ' + delCatalogo(ROLES_COM, e.rol_id, '', 'nombre'));
  if (e.producto_id){
    partes.push(delCatalogo(PRODUCTOS, e.producto_id, 'producto', 'codigo'));
  } else if (e.categoria_id){
    partes.push(delCatalogo(CATEGORIAS, e.categoria_id, 'categoría', 'nombre'));
  }
  return partes.join(' · ') || 'todo';
}

//  Hoy la base sólo calcula `monto_fijo`, y `fijar_esquema_comision`
//  rechaza los otros dos tipos con todas las letras. Pero la tabla los
//  permite desde la 0003, así que si alguna vez quedó uno cargado se dice
//  en vez de mostrarlo como cero.
function montoDe(e){
  if (e.tipo !== 'monto_fijo')
    return `<span class="chip c-bad">tipo "${esc(e.tipo)}" — todavía no se calcula</span>`;
  return `<b>${num(Number(e.monto))}</b> <span class="hint">${esc(rotuloMoneda())}</span>`;
}

function pintarEsquemas(){
  const verBajas = $('#esqVerBajas').checked;
  const f = ESQUEMAS.filter(e => verBajas || e.activo)
                    .sort((a,b) => prioridadDe(b) - prioridadDe(a) || a.id - b.id);

  $('#tblEsquemas').innerHTML = f.map(e => `
    <tr${e.activo ? '' : ' style="opacity:.55"'}>
      <td><b>${esc(e.nombre)}</b>${e.activo ? '' : ' <span class="chip c-bad">de baja</span>'}</td>
      <td>${esc(destinoDe(e))}</td>
      <td class="num">${montoDe(e)}</td>
      <td class="mono">${esc(e.vigencia_desde)}</td>
      <td class="mono">${esc(e.vigencia_hasta || '—')}</td>
      <td class="num"><span class="chip">${prioridadDe(e)}</span></td>
      <td>${e.activo
            ? `<button class="b b-sec" data-bajaesq="${e.id}">Dar de baja</button>` : ''}</td>
    </tr>`).join('') ||
    `<tr><td colspan="7" class="vacio">${esc(ERROR_ESQ
      ? 'No se pudieron leer las tarifas'
      : 'No hay tarifas cargadas')}</td></tr>`;

  $$('[data-bajaesq]', $('#tblEsquemas')).forEach(b =>
    b.onclick = () => darDeBaja(Number(b.dataset.bajaesq)));
}

$('#esqVerBajas').onchange = pintarEsquemas;

//  Los dos ejes se eligen por separado porque así los combina la base: una
//  tarifa puede ser de una persona Y de una categoría a la vez, y ésa es
//  justamente la que gana. Lo que no se puede es categoría y producto
//  juntos, ni persona y rol juntos: el formulario no lo deja armar.
function ejesTarifa(){
  const cosa = $('#esEjeCosa').value, persona = $('#esEjePersona').value;
  return {
    usuario_id  : persona === 'usuario'   ? Number($('#esUsuario').value)   : null,
    rol_id      : persona === 'rol'       ? Number($('#esRol').value)       : null,
    producto_id : cosa    === 'producto'  ? Number($('#esProducto').value)  : null,
    categoria_id: cosa    === 'categoria' ? Number($('#esCategoria').value) : null
  };
}

function reflejarTarifa(){
  const cosa    = $('#esEjeCosa').value;
  const persona = $('#esEjePersona').value;
  $('#esCategoria').parentElement.classList.toggle('oculto', cosa !== 'categoria');
  $('#esProducto').parentElement.classList.toggle('oculto', cosa !== 'producto');
  $('#esUsuario').parentElement.classList.toggle('oculto', persona !== 'usuario');
  $('#esRol').parentElement.classList.toggle('oculto', persona !== 'rol');

  const e = ejesTarifa();
  const p = prioridadDe(e);
  //  El eje elegido contra un desplegable vacío: `Number('')` es 0 y armaba una tarifa con `categoria_id: 0`;
  //  la base contestaba "Esa categoría no existe", que manda a buscar donde no es.
  //
  //  Ese desplegable está vacío por DOS causas (D87): el texto no puede decir siempre "No hay ninguna
  //  categoría", se contradiría con el cartel de arriba que dice que falló la lectura.
  const vacio = (cosa === 'categoria' && !CATEGORIAS.length)
                  ? { uno: 'ninguna categoría', varias: 'categorías', err: ERROR_CATALOGOS }
              : (cosa === 'producto'  && !PRODUCTOS.length)
                  ? { uno: 'ningún producto', varias: 'productos', err: ERROR_CATALOGOS }
              : (persona === 'usuario' && !PERSONAS.length)
                  ? { uno: 'ninguna persona', varias: 'personas', err: ERROR_PERSONAS }
              : (persona === 'rol'     && !ROLES_COM.length)
                  ? { uno: 'ningún rol', varias: 'roles', err: ERROR_CATALOGOS }
              : null;

  //  Sin destino la base rechaza la tarifa: no se deja apretar y se dice por qué.
  //  `vacio` se pregunta ANTES que `!p`: `Number('')` es 0 y `prioridadDe` trata el 0 como falsy, así que con
  //  UN SOLO eje y su desplegable vacío saldría "Elegí a quién…" sobre un desplegable sin nada que elegir.
  //  Con el producto como eje y ninguno elegido, `producto_id` sale 0 y
  //  `prioridadDe` lo ignora: con una persona elegida se fijaría una tarifa
  //  de la persona para TODO. No se deja (D216).
  const sinProducto = !vacio && cosa === 'producto' && !$('#esProducto').value;
  $('#esOk').disabled = !p || !!vacio || sinProducto;
  $('#esResumen').innerHTML = sinProducto
    ? '<span class="chip c-warn">Elegí el producto.</span>'
    : vacio
    ? `<span class="chip c-bad">${vacio.err
        ? `No se pudo leer la lista de ${vacio.varias}`
        : `No hay ${vacio.uno} para elegir`}, así que no se puede fijar una ` +
      'tarifa por ahí.</span>'
    : !p
    ? '<span class="chip c-warn">Elegí a quién o a qué se le aplica: una categoría, un producto, una persona o un rol.</span>'
    : `Se aplica a <b>${esc(destinoDe(e))}</b>. Prioridad <b>${p}</b>: ` +
      'gana sobre cualquier tarifa de prioridad menor que también aplique.';
}
['#esEjeCosa','#esEjePersona','#esCategoria','#esProducto','#esUsuario','#esRol']
  .forEach(s => $(s).onchange = reflejarTarifa);

$('#formEsquema').onsubmit = async e => {
  e.preventDefault();
  limpiar('#esqMsg');
  const ejes = ejesTarifa();
  if (!prioridadDe(ejes)) return;
  if ($('#esEjeCosa').value === 'producto' && !ejes.producto_id) return;
  $('#esOk').disabled = true;
  const { data, error } = await rpc('fijar_esquema_comision', {
    p_nombre      : $('#esNombre').value.trim(),
    p_monto       : Number($('#esMonto').value),
    p_categoria_id: ejes.categoria_id,
    p_producto_id : ejes.producto_id,
    p_usuario_id  : ejes.usuario_id,
    p_rol_id      : ejes.rol_id,
    p_desde       : $('#esDesde').value || null,
    p_motivo      : $('#esMotivo').value.trim() || null
  });
  if (error){ $('#esOk').disabled = false; aviso('#esqMsg','bad', error.message); return; }
  //  El éxito se dice ANTES de recargar: si la recarga falla, su error
  //  pisa este mensaje, que es lo que hay que ver.
  aviso('#esqMsg','ok', data.mensaje);
  ['#esNombre','#esMonto','#esDesde','#esMotivo'].forEach(s => $(s).value = '');
  $('#esEjeCosa').value = ''; $('#esEjePersona').value = '';
  $('#esProducto').value = '';
  reflejarTarifa();
  await Promise.all([cargarEsquemas(), cargarHistorialEsquemas()]);
  pintarEsquemas(); pintarHistorialEsquemas();
};

async function darDeBaja(id){
  const e = ESQUEMAS.find(x => x.id === id); if (!e) return;
  const motivo = prompt(`Dar de baja "${e.nombre}". ¿Por qué? Queda escrito.`);
  if (motivo === null) return;
  limpiar('#esqMsg');
  const { data, error } = await rpc('desactivar_esquema_comision',
    { p_esquema_id: id, p_motivo: motivo || null });
  if (error){ aviso('#esqMsg','bad', error.message); return; }
  aviso('#esqMsg','ok', data.mensaje);
  await Promise.all([cargarEsquemas(), cargarHistorialEsquemas()]);
  pintarEsquemas(); pintarHistorialEsquemas();
}

// ---------- el historial de las tarifas ----------
async function cargarHistorialEsquemas(){
  const { data, error } = await pedir(sb.from('comision_esquema_historial')
    .select('id,esquema_id,accion,nombre,tipo,monto,destino,vigencia_desde,vigencia_hasta,motivo,usuario_id,ocurrido_en')
    .order('id', { ascending: false }).limit(300));
  if (error){ ERROR_HIST = error.message; HIST_ESQ = []; aviso('#histEsqMsg','bad', error.message); return false; }
  ERROR_HIST = ''; HIST_ESQ = data || [];
  return true;
}

function pintarHistorialEsquemas(){
  $('#tblHistEsq').innerHTML = HIST_ESQ.map(h => `
    <tr>
      <td class="mono">${esc(fecha(h.ocurrido_en))}</td>
      <td><span class="chip ${h.accion === 'baja' ? 'c-bad' : 'c-ok'}">${esc(h.accion)}</span></td>
      <td><b>${esc(h.nombre || '—')}</b></td>
      <td class="num">${h.monto == null ? '—' : num(Number(h.monto))}</td>
      <td>${esc(h.destino || '—')}</td>
      <td class="mono">${esc(h.vigencia_desde || '—')} → ${esc(h.vigencia_hasta || 'sin fin')}</td>
      <td>${esc(h.motivo || '—')}</td>
      <td>${esc(nombreDe(h.usuario_id))}</td>
    </tr>`).join('') ||
    `<tr><td colspan="8" class="vacio">${esc(
      //  El RLS de `comision_esquema_historial` pide el ALCANCE `todos` de `comision.configurar`: con
      //  `configurar:propio` se ven sólo los cambios propios, y "no se cambió ninguna" afirmaría de toda la empresa (D85).
      ERROR_HIST
        ? 'No se pudo leer el historial de tarifas'
        : alcance('comision','configurar') === 'todos'
        ? 'Todavía no se cambió ninguna tarifa'
        : 'No hay cambios de tarifa visibles para vos: con tu permiso se ven ' +
          'los que hiciste vos')}</td></tr>`;
}

// =====================================================================
//  3 · LAS LIQUIDACIONES
// =====================================================================
//  Esta tarjeta no se esconde: el RLS ya se encarga de que cada uno vea
//  las suyas y nada más. Por eso el texto de la tabla vacía dice "no hay
//  para mostrar" y no "no se generó ninguna": desde acá no se sabe.
async function cargarLiquidaciones(){
  const { data, error } = await pedir(sb.from('v_liquidaciones')
    .select('id,tipo,usuario_id,persona,periodo_desde,periodo_hasta,total,moneda,estado,items,generada_en,generada_por,pagada_en,pagada_por,caja,notas')
    .order('id', { ascending: false }).limit(300));
  if (error){ ERROR_LIQS = error.message; LIQS = []; aviso('#liqMsg','bad', error.message); return false; }
  ERROR_LIQS = ''; LIQS = data || [];
  return true;
}

//  De qué caja salió la plata. `v_liquidaciones` llega al nombre por un LEFT JOIN a `cajas`, cuyo RLS es
//  `puede('caja','ver')` (D84): sin ese permiso la fila llega entera y sólo esta columna viene en null (le
//  pasa al rol `Motoquero`, que tiene `liquidacion.ver:propio` y no `caja.ver`).
//
//  Ese null tiene DOS causas que no se dicen igual (D87): se pagó sin registrar la caja, o la caja está y no
//  la podés ver. Omitirla en silencio deja creyendo que el dato no existe.
function cajaDe(l){
  if (l.caja) return ' · ' + esc(l.caja);
  return puede('caja','ver')
    ? ' · sin caja registrada'
    //  Sin el permiso los DOS caminos dan el mismo null, así que el texto no puede elegir uno: decir sólo "no
    //  podés ver de qué caja salió" afirma que hay una caja que quizá no existe (D87).
    : ' · no podés ver de qué caja salió, ni si quedó registrada';
}

function pintarLiquidaciones(){
  const est = $('#liqEstado').value, tip = $('#liqTipo').value;
  const f = LIQS.filter(l => (!est || l.estado === est) && (!tip || l.tipo === tip));
  //  Ojo: no agregar un `&& !ERROR_LIQS` para no ofrecer "Pagar" sobre una lista no releída. `cargarLiquidaciones`
  //  VACÍA `LIQS` cuando falla, así que no hay fila que traiga botón: lo que protege de pagar dos veces es ese
  //  vaciado y el aviso de `#liqDetMsg`.
  const puedePagar  = puede('liquidacion','pagar');
  const puedeAnular = puede('liquidacion','generar');

  $('#tblLiquidaciones').innerHTML = f.map(l => `
    <tr data-liq="${l.id}" style="cursor:pointer${l.estado === 'anulada' ? ';opacity:.55' : ''}">
      <td class="mono">#${l.id}</td>
      <td>${esc(NOMBRE_TIPO[l.tipo] || l.tipo)}</td>
      <td><b>${esc(l.persona || '—')}</b></td>
      <td class="mono">${esc(l.periodo_desde)} → ${esc(l.periodo_hasta)}</td>
      <td class="num">${num(Number(l.items))}</td>
      <td class="num"><b>${num(Number(l.total))}</b> <span class="hint">${esc(l.moneda)}</span></td>
      <td><span class="chip ${CHIP_LIQ[l.estado] || ''}">${esc(l.estado)}</span></td>
      <td class="mono">${esc(fecha(l.generada_en))}<br>
          <span class="hint">${esc(l.generada_por || '—')}</span></td>
      <td class="mono">${l.pagada_en
          ? `${esc(fecha(l.pagada_en))}<br><span class="hint">${esc(l.pagada_por || '—')}${
              cajaDe(l)}</span>` : '—'}</td>
      <td><div class="fila-acc">
        ${puedePagar && l.estado !== 'pagada' && l.estado !== 'anulada' && Number(l.total) > 0
          ? `<button class="b b-pri" data-pagarliq="${l.id}">Pagar</button>` : ''}
        ${puedeAnular && l.estado !== 'pagada' && l.estado !== 'anulada'
          ? `<button class="b b-sec" data-anularliq="${l.id}">Anular</button>` : ''}
      </div></td>
    </tr>`).join('') ||
    `<tr><td colspan="10" class="vacio">${esc(
      LIQS.length ? 'Nada con ese filtro'
      : ERROR_LIQS ? 'No se pudieron leer las liquidaciones'
      //  Misma regla que las otras dos tablas: el RLS de `liquidaciones` deja pasar las propias SIN pedir permiso,
      //  así que sin `liquidacion.ver` "no hay ninguna" afirmaría sobre la lista de una persona.
      : alcance('liquidacion','ver') === 'todos'
        ? 'No hay liquidaciones para mostrar'
        : 'No hay liquidaciones tuyas para mostrar')}</td></tr>`;

  $$('[data-liq]', $('#tblLiquidaciones')).forEach(tr =>
    tr.onclick = ev => {
      if (el(ev.target).closest('button')) return;
      abrirLiquidacion(Number(tr.dataset.liq));
    });
  $$('[data-pagarliq]', $('#tblLiquidaciones')).forEach(b =>
    b.onclick = () => abrirLiquidacion(Number(b.dataset.pagarliq)));
  $$('[data-anularliq]', $('#tblLiquidaciones')).forEach(b =>
    b.onclick = () => anularLiquidacion(Number(b.dataset.anularliq)));
}

$('#liqEstado').onchange = pintarLiquidaciones;
$('#liqTipo').onchange   = pintarLiquidaciones;

// ---------- el detalle, y el pago ----------
//  `LIQ_ABIERTA` se mueve recién cuando la tarjeta terminó de pintarse con la liquidación nueva: si se
//  moviera antes y el detalle fallara, quedaría la anterior con su botón de pagar, que pagaría otra. Es plata.
async function abrirLiquidacion(id, silencioso?){
  const l = LIQS.find(x => x.id === id);
  //  Pedir una liquidación que no está en la lista tampoco es un silencio: la lista no se pudo releer o se acaba de anular.
  if (!l){
    cerrarDetalle();
    //  Dos causas otra vez (D87). Con `ERROR_LIQS` la tabla ya dice que la lectura falló y `#liqMsg` tiene el
    //  mensaje de la base (D68): escribir "no está en la lista" lo PISA y se contradice con la tabla.
    if (!ERROR_LIQS)
      aviso('#liqMsg','warn', `La liquidación #${id} no está en la lista de arriba, ` +
        'así que no se puede abrir su detalle. Puede que no te la deje ver tu ' +
        'permiso, o que la acabes de anular.');
    return;
  }
  if (!silencioso) limpiar('#liqDetMsg');

  //  ENTERO: una liquidación de quince días puede tener más de mil ítems y el servidor corta en mil sin
  //  avisar (D122). Lo que se revisa antes de pagar no puede ser una parte.
  const { data, error } = await pedirTodo(() => sb.from('liquidacion_items')
    .select('id,referencia_tipo,referencia_id,descripcion,monto,ocurrido_en')
    .eq('liquidacion_id', id), { por: 'id', orden: ordenarPor('ocurrido_en', 'id') });

  //  La tarjeta se DESTAPA antes de cualquier `return`: `#liqDetMsg` vive ADENTRO de `#cardLiqDetalle`, y un
  //  cartel escrito en un nodo `display:none` no se ve. Igual que `#movsMsg` en `#cardMovsCC` (Cuentas) y
  //  `#recCatMsg` en `#cardRemito` (Recepción).
  $('#cardLiqDetalle').classList.remove('oculto');
  if (error){
    //  `LIQ_ABIERTA` en null y el formulario cerrado: el botón de pagar no
    //  puede quedar vivo sobre una liquidación cuyo detalle no se leyó.
    LIQ_ABIERTA = null;
    $('#formPagoLiq').classList.add('oculto');
    $('#liqDetTit').textContent = `Liquidación #${id}`;
    $('#liqDetNotas').classList.add('oculto');
    $('#tblLiqDetalle').innerHTML =
      '<tr><td colspan="4" class="vacio">No se pudo leer el detalle</td></tr>';
    aviso('#liqDetMsg','bad', error.message);
    return;
  }

  LIQ_ABIERTA = id;
  $('#liqDetTit').innerHTML =
    `Liquidación #${l.id} — ${esc(l.persona || '')}: <b>${num(Number(l.total))} ${esc(l.moneda)}</b>` +
    ` <span class="chip ${CHIP_LIQ[l.estado] || ''}">${esc(l.estado)}</span>` +
    ` <span class="hint">${esc(l.periodo_desde)} a ${esc(l.periodo_hasta)}</span>`;
  $('#liqDetNotas').textContent = l.notas || '';
  $('#liqDetNotas').classList.toggle('oculto', !l.notas);

  $('#tblLiqDetalle').innerHTML = (data || []).map(i => `
    <tr>
      <td class="mono">${esc(fecha(i.ocurrido_en))}</td>
      <td>${esc(i.descripcion || '—')}</td>
      <td class="mono">${esc(i.referencia_tipo)} #${i.referencia_id}</td>
      <td class="num"><b>${num(Number(i.monto))}</b></td>
    </tr>`).join('') ||
    `<tr><td colspan="4" class="vacio">${esc(
      //  La tabla de arriba pinta `items`, un `count(*)` dentro de la vista, y este detalle es otra consulta. Por
      //  permisos no se contradicen (el RLS de `liquidacion_items` hereda el de `liquidaciones`), así que si el
      //  count dice que hay y acá no llegó ninguno, el detalle no se leyó entero: no decir "no tiene ítems".
      Number(l.items) > 0
        ? `Esta liquidación dice tener ${l.items} ${Number(l.items) === 1 ? 'ítem' : 'ítems'} y el detalle no trajo ` +
          'ninguno: no se pudo leer entero.'
        : 'Esta liquidación no tiene ítems')}</td></tr>`;

  //  Pagar sólo tiene sentido si hay algo que pagar y todavía no se pagó.
  //  Que la caja tenga el saldo lo mira la base (D59), no esta pantalla.
  const pagable = puede('liquidacion','pagar') &&
                  l.estado !== 'pagada' && l.estado !== 'anulada' && Number(l.total) > 0;
  //  La habilitación por caja (D93). Dos preguntas, las dos hacen falta: de qué cajas de la lista puedo sacar
  //  la plata, y si puedo sacarla de la de la configuración (lo que pasa al no elegir ninguna). Si ninguna da,
  //  el formulario no se dibuja, con el cartel que dice cuál falta.
  const cajasMias = CAJAS_LIQ.filter(c => puedoOperar(c.id) !== false);
  const laDeSiempre = puedoActo('liquidacion') !== false;
  const hayDeDonde = laDeSiempre || cajasMias.length > 0;
  if (pagable && !hayDeDonde)
    avisoHtml('#liqDetMsg','warn',
      noHabilitado(cajaDelActo('liquidacion'), 'pagar desde') + ' La liquidación ' +
      'sale de esa caja salvo que se elija otra, y no estás habilitado en ninguna ' +
      'de las que ves. La tiene que pagar alguien habilitado.');
  $('#formPagoLiq').classList.toggle('oculto', !(pagable && hayDeDonde));
  if (pagable && hayDeDonde){
    $('#plCaja').innerHTML = (laDeSiempre
      ? '<option value="">La de siempre (la que dice la configuración)</option>' : '') +
      cajasMias.map(c => `<option value="${c.id}">${esc(c.nombre)}</option>`).join('');
    //  No esconder el select entero con la lista vacía: TRES causas se veían igual (no tengo `caja.ver`, la
    //  lectura falló, no hay cajas activas) y el pago sale igual por la caja de la configuración. "La de siempre"
    //  siempre es válida: el select se queda y cambia el texto de al lado (D87).
    $('#plCaja').parentElement.classList.remove('oculto');
    const nota = laDeSiempre ? notaCaja()
      //  Y la cuarta causa, que no es ninguna de las tres de `notaCaja()`: la
      //  caja de la configuración existe y se leyó, pero no la puedo operar,
      //  así que "la de siempre" no está entre las opciones y hay que elegir.
      : 'No estás habilitado para pagar desde la caja que dice la configuración, ' +
        'así que hay que elegir una de las tuyas.';
    $('#plCajaNota').textContent = nota;
    $('#plCajaNota').classList.toggle('oculto', !nota);
    //  El botón se re-arma cada vez que esta tarjeta se vuelve a pintar: si se apaga cuando la base no
    //  contesta, lo enciende la recarga, que trae el estado real.
    $('#plOk').disabled = false;
  }
}

//  Por qué no hay cajas para elegir, cuando no las hay. Las tres causas
//  terminan en el mismo pago —el de la caja de la configuración— pero se
//  arreglan distinto, y la última no se arregla: es así.
function notaCaja(){
  if (CAJAS_LIQ.length) return '';
  if (!puede('caja','ver'))
    return 'No podés ver las cajas, así que no elegís de cuál sale: el pago va a ' +
           'salir de la que dice la configuración.';
  if (ERROR_CAJAS)
    return 'No se pudo leer la lista de cajas, así que no se puede elegir: el pago ' +
           'va a salir de la que dice la configuración.';
  return 'No hay ninguna caja activa para elegir: el pago va a salir de la que ' +
         'dice la configuración.';
}

function cerrarDetalle(){
  $('#cardLiqDetalle').classList.add('oculto');
  $('#formPagoLiq').classList.add('oculto');
  LIQ_ABIERTA = null;
}
$('#liqDetCerrar').onclick = cerrarDetalle;

$('#plOk').onclick = async () => {
  if (!LIQ_ABIERTA) return;
  limpiar('#liqDetMsg');
  const id = LIQ_ABIERTA, caja = $('#plCaja').value;
  $('#plOk').disabled = true;
  const { data, error } = await rpc('pagar_liquidacion', {
    p_liquidacion_id: id,
    p_caja_id       : caja ? Number(caja) : null
  });
  //  EL BOTÓN NO VUELVE CUANDO LA BASE NO CONTESTÓ. Un rechazo deja la operación en un estado que la pantalla
  //  no sabe leer: pudo haberse hecho o no. Devolver el botón invitaría a repetir una escritura que quizá ya
  //  salió, y acá eso es pagar dos veces. El cartel ofrece IR A MIRAR: volver a entrar recarga la lista y
  //  re-arma el botón.
  //
  //  Se distingue por el CÓDIGO y no por el texto (D68): `RECHAZO` lo pone el envoltorio de `rpc()` y no existe
  //  en PostgREST, así que un "no" de la base (reintentable) devuelve el botón como siempre.
  if (error?.code !== RECHAZO) $('#plOk').disabled = false;
  if (error){ aviso('#liqDetMsg','bad', error.message); return; }
  aviso('#liqDetMsg','ok', data.mensaje);
  //  Pagar mueve los ítems de "en liquidación" a "pagada": la tabla de
  //  arriba se recarga o queda mostrando lo de antes.
  //  La misma pregunta que abre la tarjeta, no una más chica: con
  //  `configurar:todos` y sin `comision.ver` la tabla está a la vista y hay que
  //  recargarla igual.
  const [releida] = await Promise.all([cargarLiquidaciones(),
                     veComisiones_() ? cargarMisComisiones() : null]);
  if (veComisiones_()) pintarFilasComisiones();
  pintarLiquidaciones();
  //  Si la lista no se pudo releer, lo de abajo es lo de ANTES: la liquidación figuraría en borrador con su
  //  botón de pagar sobre una ya pagada. No se reabre el detalle y se dice por qué.
  if (!releida){
    //  En `#liqMsg`, que vive AFUERA de la tarjeta.
    //  Ojo: escribir en `#liqDetMsg` y llamar a `cerrarDetalle()` deja el cartel en un nodo `display:none`;
    //  éste es el único camino que mueve plata.
    cerrarDetalle();
    aviso('#liqMsg','warn', 'El pago se registró, pero la lista no se pudo volver ' +
      'a leer. Volvé a entrar a la pestaña para ver cómo quedó.');
    return;
  }
  await abrirLiquidacion(id, true);
};

async function anularLiquidacion(id){
  const l = LIQS.find(x => x.id === id); if (!l) return;
  const motivo = prompt(
    `Anular la liquidación #${id} de ${l.persona}. ${Number(l.items) === 1 ? 'Su ítem vuelve a quedar disponible'
      : `Sus ${l.items} ítems vuelven a quedar disponibles`} para la próxima.` +
    ' ¿Por qué? Queda escrito.');
  if (motivo === null) return;
  limpiar('#liqMsg');
  const { data, error } = await rpc('anular_liquidacion',
    { p_liquidacion_id: id, p_motivo: motivo || null });
  if (error){ aviso('#liqMsg','bad', error.message); return; }
  aviso('#liqMsg','ok', data.mensaje);
  const [releida] = await Promise.all([cargarLiquidaciones(),
                     veComisiones_() ? cargarMisComisiones() : null]);
  if (veComisiones_()) pintarFilasComisiones();
  pintarLiquidaciones();
  if (!releida){
    aviso('#liqMsg','warn', 'La liquidación se anuló, pero la lista no se pudo ' +
      'volver a leer: lo que se ve es lo de antes. Volvé a entrar a la pestaña.');
    cerrarDetalle();
    return;
  }
  if (LIQ_ABIERTA === id) await abrirLiquidacion(id, true);
}

// ---------- generar la quincena ----------
//  Las fechas se prellenan, no se imponen: el período lo termina de decidir
//  quien liquida, y la base rechaza liquidar un período que no terminó.
function quincena(cual){
  const hoy = new Date();
  const y = hoy.getFullYear(), m = hoy.getMonth(), d = hoy.getDate();
  //  0 = la última quincena cerrada; 1 = la anterior a ésa.
  let desde, hasta;
  if (d > 15){ desde = new Date(y, m, 1);      hasta = new Date(y, m, 15); }
  else       { desde = new Date(y, m - 1, 16); hasta = new Date(y, m, 0);  }
  if (cual === 1){
    hasta = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate() - 1);
    desde = hasta.getDate() === 15
      ? new Date(hasta.getFullYear(), hasta.getMonth(), 1)
      : new Date(hasta.getFullYear(), hasta.getMonth(), 16);
  }
  const iso = f => `${f.getFullYear()}-${String(f.getMonth()+1).padStart(2,'0')}-${
                     String(f.getDate()).padStart(2,'0')}`;
  $('#glDesde').value = iso(desde);
  $('#glHasta').value = iso(hasta);
}
$('#glQuincenaUlt').onclick = () => quincena(0);
$('#glQuincenaAnt').onclick = () => quincena(1);

$('#formGenerarLiq').onsubmit = async e => {
  e.preventDefault();
  limpiar('#genMsg');
  //  Sin este freno, dos clics seguidos generan dos liquidaciones: la
  //  segunda sale en cero (los ítems ya se los llevó la primera) y hay
  //  que anularla a mano.
  $('#glOk').disabled = true;
  const { data, error } = await rpc('generar_liquidacion', {
    p_usuario_id: Number($('#glPersona').value),
    p_tipo      : $('#glTipo').value,
    p_desde     : $('#glDesde').value,
    p_hasta     : $('#glHasta').value
  });
  //  Igual que el botón de pagar: dos clics seguidos generan dos liquidaciones y la segunda hay que anularla a
  //  mano. A éste lo vuelve a armar `cargarComisiones` sola (`disabled` según haya personas).
  if (error?.code !== RECHAZO) $('#glOk').disabled = false;
  if (error){ aviso('#genMsg','bad', error.message); return; }
  aviso('#genMsg', data.items ? 'ok' : 'warn', data.mensaje);
  const [releida] = await Promise.all([cargarLiquidaciones(),
                     veComisiones_() ? cargarMisComisiones() : null]);
  if (veComisiones_()) pintarFilasComisiones();
  pintarLiquidaciones();
  if (!releida){
    aviso('#genMsg','warn', 'La liquidación se generó, pero la lista de arriba no ' +
      'se pudo volver a leer, así que no aparece ahí ni se puede abrir su ' +
      'detalle. Volvé a entrar a la pestaña.');
    return;
  }
  //  Una liquidación sin ítems se genera igual y queda en cero: se abre
  //  para que se vea que está vacía, en vez de dejar dudando.
  await abrirLiquidacion(data.id, true);
};
